import React, { useEffect, useMemo, useRef, useState } from "react";
import { MapContainer, TileLayer, Marker, Circle, useMap } from "react-leaflet";
import L from "leaflet";
import "leaflet/dist/leaflet.css";
import { Search, Crosshair, Copy, ExternalLink, Eye } from "lucide-react";

// Pin marker icon — riusa lo stile della pagina di gioco se presente, altrimenti default
const PIN_ICON = L.divIcon({
  className: "",
  html: `<div style="
    width:32px;height:32px;border-radius:50% 50% 50% 0;
    background:#F2A93B;border:3px solid #FFF;
    box-shadow:0 4px 8px rgba(62,39,35,.35);
    transform:rotate(-45deg); display:flex;align-items:center;justify-content:center;">
    <div style="width:10px;height:10px;background:#3E2723;border-radius:50%;"></div>
  </div>`,
  iconSize: [32, 32],
  iconAnchor: [16, 32],
});

function ClickHandler() {
  // Click sulla mappa disabilitato: usare GPS ≤5m, ricerca indirizzo o coordinate manuali.
  return null;
}

function Recenter({ position }) {
  const map = useMap();
  useEffect(() => {
    if (position) map.setView(position, map.getZoom() < 13 ? 14 : map.getZoom(), { animate: true });
  }, [position]); // eslint-disable-line
  return null;
}

/**
 * Selettore di posizione su mappa interattiva.
 * Props:
 *   value: { lat:number, lng:number }
 *   onChange: ({lat,lng}) => void
 *   radius_m: raggio del cerchio overlay (opzionale, per anteprima)
 *   height: altezza in pixel (default 360)
 */
export default function LocationPickerMap({ value, onChange, radius_m = 50, height = 360, gpsAccuracy = null, showAccuracyInfo = false }) {
  const [searchQuery, setSearchQuery] = useState("");
  const [searching, setSearching] = useState(false);
  const [searchError, setSearchError] = useState("");
  const debounceRef = useRef(null);

  const lat = Number(value?.lat) || 41.9028;
  const lng = Number(value?.lng) || 12.4964;
  const center = useMemo(() => [lat, lng], [lat, lng]);

  // Nominatim OSM geocoding (gratis, no key). Debounce sul submit.
  const doSearch = async (q) => {
    if (!q || q.trim().length < 3) return;
    setSearching(true);
    setSearchError("");
    try {
      const url = `https://nominatim.openstreetmap.org/search?format=json&limit=1&q=${encodeURIComponent(q)}`;
      const r = await fetch(url, { headers: { "Accept-Language": "it" } });
      const data = await r.json();
      if (!data || !data.length) {
        setSearchError("Indirizzo non trovato");
        return;
      }
      const item = data[0];
      onChange({ lat: parseFloat(item.lat), lng: parseFloat(item.lon) });
    } catch {
      setSearchError("Errore di rete");
    } finally {
      setSearching(false);
    }
  };

  const onSearchSubmit = (e) => {
    e.preventDefault();
    if (debounceRef.current) clearTimeout(debounceRef.current);
    doSearch(searchQuery);
  };

  const useGeolocation = () => {
    if (!navigator.geolocation) {
      setSearchError("Geolocalizzazione non disponibile");
      return;
    }
    setSearching(true);
    setSearchError("");
    navigator.geolocation.getCurrentPosition(
      (pos) => {
        onChange({ lat: pos.coords.latitude, lng: pos.coords.longitude });
        setSearching(false);
      },
      () => {
        setSearchError("Permesso geolocalizzazione negato");
        setSearching(false);
      },
      { enableHighAccuracy: true, timeout: 8000 }
    );
  };

  return (
    <div className="space-y-2" data-testid="location-picker">
      <form onSubmit={onSearchSubmit} className="flex gap-2" data-testid="location-search-form">
        <div className="relative flex-1">
          <Search size={16} className="absolute left-3 top-1/2 -translate-y-1/2 text-ink-500"/>
          <input
            type="text"
            value={searchQuery}
            onChange={(e) => setSearchQuery(e.target.value)}
            placeholder="Cerca un indirizzo o luogo (es. Colosseo, Roma)…"
            className="h-11 w-full pl-10 pr-3 rounded-2xl border border-outline bg-white focus:outline-none focus:ring-2 focus:ring-gold text-sm"
            data-testid="location-search-input"
          />
        </div>
        <button
          type="submit"
          disabled={searching}
          className="px-4 h-11 rounded-2xl bg-ocean text-white font-bold text-sm disabled:opacity-50"
          data-testid="location-search-btn"
        >
          {searching ? "..." : "Cerca"}
        </button>
        <button
          type="button"
          onClick={useGeolocation}
          disabled={searching}
          className="h-11 w-11 rounded-2xl bg-forest text-white flex items-center justify-center hover:bg-forest/90 disabled:opacity-50 shadow-tactile"
          title="Usa la mia posizione GPS attuale (richiede ±10m)"
          data-testid="location-geolocate-btn"
        >
          <Crosshair size={18}/>
        </button>
      </form>
      {searchError && (
        <div className="text-xs text-compass font-bold" data-testid="location-search-error">{searchError}</div>
      )}

      {/* Banner informativo: mappa read-only */}
      <div className="bg-ocean-light border-2 border-ocean/40 rounded-2xl p-3 text-xs space-y-1" data-testid="location-info-banner">
        <div className="font-bold text-ocean">🎯 Posiziona con massima precisione</div>
        <ul className="ml-4 list-disc space-y-0.5 text-ink-700">
          <li><strong>Consigliato:</strong> pulsante GPS 📍 quando sei sul posto (richiede ±10m)</li>
          <li><strong>Cerca</strong> l&apos;indirizzo nella barra in alto</li>
          <li><strong>Coordinate manuali</strong> negli input Lat/Lng sotto</li>
        </ul>
        <div className="italic text-ink-500 pt-1">💡 La mappa è solo visualizzazione, non puoi cliccarci sopra</div>
      </div>

      {/* Indicatore accuratezza GPS */}
      {showAccuracyInfo && gpsAccuracy !== null && (
        <div
          className={`p-3 rounded-2xl border-2 font-bold text-sm ${
            gpsAccuracy <= 10
              ? "bg-forest/10 border-forest text-forest"
              : gpsAccuracy <= 20
              ? "bg-yellow-50 border-yellow-400 text-yellow-800"
              : "bg-red-50 border-red-400 text-compass"
          }`}
          data-testid="location-accuracy-info"
        >
          <div className="flex items-center justify-between mb-1">
            <span>📡 Accuratezza GPS</span>
            <span className="font-mono text-base">±{Math.round(gpsAccuracy)}m {gpsAccuracy <= 10 ? "✅" : "❌"}</span>
          </div>
          {gpsAccuracy <= 10 && <div>🟢 Ottimo — precisione accettata</div>}
          {gpsAccuracy > 10 && gpsAccuracy <= 20 && <div>🟡 Insufficiente — serve ≤10m</div>}
          {gpsAccuracy > 20 && <div>🔴 Scarso — serve ≤10m</div>}
          {gpsAccuracy > 10 && (
            <div className="mt-2 text-xs font-normal bg-white/70 rounded-xl p-2 border border-red-200 space-y-1">
              <div className="font-bold text-compass">Come migliorare a ≤10m:</div>
              <ul className="list-disc ml-4 space-y-0.5">
                <li>Esci all&apos;aperto (piazza, strada larga, parco)</li>
                <li>Cielo visibile, lontano da edifici/alberi/tettoie</li>
                <li>Aspetta 20-30s per stabilizzazione GPS</li>
                <li>NO indoor: il GPS non funziona dentro edifici</li>
                <li>Se impossibile: usa coordinate manuali precise</li>
              </ul>
            </div>
          )}
        </div>
      )}

      <div
        className="rounded-2xl overflow-hidden border-2 border-outline bg-parchment-dark"
        style={{ height }}
        data-testid="location-picker-map"
      >
        <MapContainer
          center={center}
          zoom={13}
          style={{ height: "100%", width: "100%" }}
          scrollWheelZoom
        >
          <TileLayer
            attribution='&copy; OpenStreetMap'
            url="https://{s}.tile.openstreetmap.org/{z}/{x}/{y}.png"
          />
          <Recenter position={center} />
          <Marker position={center} icon={PIN_ICON} />
          {radius_m > 0 && (
            <Circle
              center={center}
              radius={Number(radius_m) || 50}
              pathOptions={{ color: "#F2A93B", fillColor: "#F2A93B", fillOpacity: 0.12, weight: 2 }}
            />
          )}
        </MapContainer>
      </div>

      <div className="space-y-2">
        <div className="flex items-center justify-between text-xs">
          <div className="font-mono font-bold text-ink-800">📍 {lat.toFixed(6)}, {lng.toFixed(6)}</div>
          <button
            type="button"
            onClick={() => {
              navigator.clipboard?.writeText(`${lat.toFixed(6)}, ${lng.toFixed(6)}`);
            }}
            className="text-xs px-3 py-1 rounded-full bg-ocean text-white font-bold flex items-center gap-1 hover:bg-ocean/80"
            data-testid="location-copy-btn"
          >
            <Copy size={12}/> Copia
          </button>
        </div>
        <div className="grid grid-cols-2 gap-2">
          <a
            href={`https://www.google.com/maps?q=${lat},${lng}&z=18`}
            target="_blank"
            rel="noopener noreferrer"
            className="text-center px-3 py-2 rounded-2xl bg-ocean text-white text-xs font-bold flex items-center justify-center gap-1 hover:bg-ocean/80"
            data-testid="location-verify-gmaps"
          >
            <ExternalLink size={12}/> Verifica su Google Maps
          </a>
          <a
            href={`https://www.google.com/maps/@?api=1&map_action=pano&viewpoint=${lat},${lng}`}
            target="_blank"
            rel="noopener noreferrer"
            className="text-center px-3 py-2 rounded-2xl bg-forest text-white text-xs font-bold flex items-center justify-center gap-1 hover:bg-forest/90"
            data-testid="location-verify-streetview"
          >
            <Eye size={12}/> Street View
          </a>
        </div>
        <div className="italic text-ink-500 text-center text-xs">Verifica che la posizione sia corretta prima di salvare</div>
      </div>
    </div>
  );
}
